import React from "react";
import { ProgramHeader } from "./ProgramHeader";
import { familyContactLabel } from "../server/claim-validation.js";

// The interim admin view, until the Operations Console exists (March 2027).
// THREE things, all of which a human has to decide or initiate:
//
//   1. Issue a Channel A claim link for a reference number (IssueLinkCard).
//      This SENDS REAL EMAIL the moment it is pressed — no preview, no draft —
//      so the control says so on its face.
//   2. Mark a payment received.
//   3. Resolve a flagged sponsor conflict, or a contest between claims.
//
// Item 1 was listed here for months while no control for it existed anywhere
// in this file — the endpoint was live, the UI never wired. The comment is
// very likely why nobody noticed. If a line here claims a capability, the
// capability has to be in the file underneath it.
//
// The key is held in sessionStorage and sent as an X-Admin-Key HEADER, never as
// a query string. A key in the URL leaks into browser history, bookmarks and
// any Referer header the page emits; sessionStorage also clears when the tab
// closes, which suits a shared office machine.

const KEY_STORAGE = "sponsorclaim.adminKey";

export interface ClaimSummary {
  itemId: string;
  refNumber: string;
  program: string;
  status: string;
  channel: string;
  entity: string;
  contactName: string;
  contactEmail: string;
  paymentMethod: string;
  squareOrderNumber: string;
  paymentNote: string;
  paymentReceived: boolean;
  paymentReceivedAt: string;
  paymentReceivedBy: string;
  conflictFlagged: boolean;
  conflictResolvedAt: string;
  conflictResolvedBy: string;
  conflictResolution: string;
  notes: string;
  submittedAt: string;
  createdAt: string;
  contactPhone: string;
  claimLinkIssuedBy: string;
  claimLinkIssuedAt: string;
  resolutionNotifiedAt: string;
  notificationSuppressed: boolean;
  suppressedBy: string;
  suppressedAt: string;
  suppressReason: string;
  familyContact: string;
  familyContactOther: string;
}

function readStoredKey(): string {
  try {
    return sessionStorage.getItem(KEY_STORAGE) ?? "";
  } catch {
    return "";
  }
}

/**
 * Carries the parsed response body, not just a message.
 *
 * The contest guard returns a `contest` field alongside its error, and the
 * client needs to SEE that field to recover — a bare Error string would
 * leave the admin staring at a message with nothing to click.
 */
class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: Record<string, unknown>
  ) {
    super(message);
  }
}

async function api<T>(path: string, key: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      "X-Admin-Key": key,
      ...(init.headers ?? {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError(
      (body as { error?: string }).error ?? `Request failed (${res.status}).`,
      res.status,
      body as Record<string, unknown>
    );
  }
  return body as T;
}

function shortDate(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

/** What POST /admin/claim-links hands back. Mirrors StaffLinkResult on the server. */
export interface IssuedLink {
  link: string;
  referenceNumber: string;
  emailed: boolean;
  emailError?: string;
}

/**
 * The result panel for a freshly issued Channel A link.
 *
 * Pulled out as its own component so it can be rendered and asserted directly
 * — this file's tests are server-rendered with no jsdom, so a panel that only
 * ever appears after a click would otherwise be untestable here.
 *
 * The link is shown in a READ-ONLY INPUT rather than as text. If the clipboard
 * API is unavailable (an older browser, a locked-down machine, an insecure
 * origin) the Copy button silently does nothing, and select-all in a field is
 * the fallback that always works. Losing a just-issued link to a dead button
 * is not an acceptable failure on a shared office machine.
 */
export function IssueResultView({
  result,
  onCopy,
  copied = false,
}: {
  result: IssuedLink;
  onCopy?: () => void;
  copied?: boolean;
}): React.ReactElement {
  return (
    <div className="mt-3 rounded-md border border-slate-300 bg-slate-50 p-3">
      {result.emailed ? (
        <p className="text-sm text-slate-800">
          Claim link issued for{" "}
          <strong className="font-mono">{result.referenceNumber}</strong> and{" "}
          <strong>emailed</strong>.
        </p>
      ) : (
        /*
          The link WORKS. Only the email did not go out. This has to read as
          "you have one thing left to do", not as a failure — the server
          deliberately returns success here precisely so the link is not lost.
        */
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
          <p className="text-sm font-semibold text-amber-900">
            Link created — but the email did not send
          </p>
          <p className="mt-1 text-sm text-amber-900">
            The link below is live and works. Copy it and send it to the sponsor
            yourself.
          </p>
          {result.emailError && (
            <p className="mt-1 text-xs text-amber-800">{result.emailError}</p>
          )}
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input
          className="field-input flex-1 font-mono text-xs"
          readOnly
          value={result.link}
          onFocus={(e) => e.currentTarget.select()}
          aria-label={`Claim link for ${result.referenceNumber}`}
        />
        <button type="button" className="btn-secondary" onClick={onCopy}>
          {copied ? "Copied" : "Copy link"}
        </button>
      </div>
    </div>
  );
}

/**
 * Channel A — issue a claim link for a reference number.
 *
 * Sends real email on press. The button and the line beneath it both say so,
 * because there is no preview, no draft and no undo: the sponsor's copy is
 * gone the moment this succeeds.
 */
export function IssueLinkCard({
  adminKey,
  onIssued,
}: {
  adminKey: string;
  /** Refreshes the claims list, so the new "issued" row appears immediately. */
  onIssued?: () => void;
}): React.ReactElement {
  const [refNumber, setRefNumber] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [working, setWorking] = React.useState(false);
  const [error, setError] = React.useState("");
  const [result, setResult] = React.useState<IssuedLink | null>(null);
  const [copied, setCopied] = React.useState(false);

  const ready = refNumber.trim() !== "" && email.trim() !== "";

  async function issue() {
    setWorking(true);
    setError("");
    setResult(null);
    setCopied(false);
    try {
      const out = await api<IssuedLink>("/admin/claim-links", adminKey, {
        method: "POST",
        body: JSON.stringify({
          referenceNumber: refNumber.trim(),
          email: email.trim(),
        }),
      });
      setResult(out);
      // Cleared only on success, so a rejected reference stays in the box to
      // be corrected rather than making the admin retype it.
      setRefNumber("");
      setEmail("");
      onIssued?.();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setWorking(false);
    }
  }

  function copy() {
    if (!result) return;
    try {
      void navigator.clipboard?.writeText(result.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* Clipboard blocked — the read-only field above is the fallback. */
    }
  }

  return (
    <div className="section-card">
      <h2 className="font-semibold text-slate-800">Issue a claim link</h2>
      <p className="field-hint mt-1">
        Channel A — for a sponsor who has the reference number but no printout, or
        who asked staff to send the link directly.
      </p>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="field-label" htmlFor="issue-ref">
            Reference number
          </label>
          <input
            id="issue-ref"
            className="field-input font-mono"
            placeholder="MDBS-2027-0347"
            value={refNumber}
            onChange={(e) => setRefNumber(e.target.value)}
          />
        </div>
        <div>
          <label className="field-label" htmlFor="issue-email">
            Sponsor email
          </label>
          <input
            id="issue-email"
            className="field-input"
            type="email"
            autoComplete="off"
            placeholder="post12@example.org"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          className="btn-primary"
          disabled={working || !ready}
          onClick={() => void issue()}
        >
          {working ? "Issuing…" : "Issue and email claim link"}
        </button>
        <span className="field-hint">
          Sends the link to that address <strong>immediately</strong>. There is no
          preview or draft step.
        </span>
      </div>

      {error && <p className="field-error">{error}</p>}
      {result && <IssueResultView result={result} onCopy={copy} copied={copied} />}
    </div>
  );
}

/**
 * What the FAMILY themselves reported as the sponsor, from tracker columns
 * 45/46 — the value behind the resolve option "Use the family's entry".
 *
 * Entirely separate from the `familyContact` field on ClaimSummary (Round 30),
 * which is a different fact from a different source: whether the SPONSOR has
 * been in touch with the student's family. Same word, different subject; they
 * are shown in different parts of the card and must never be merged.
 *
 * THREE states, deliberately not two. "not reported" is a definite statement
 * about the application, and a director resolving a conflict will act on it —
 * so it must never stand in for "the tracker could not be read".
 */
export type SelfReportedView =
  | { state: "loading" }
  | { state: "unavailable" }
  /** `value` empty means the family genuinely entered no sponsor. */
  | { state: "known"; value: string };

/** The /admin/self-reported-sponsors payload. `read` is which trackers answered. */
export interface SelfReportedIndex {
  sponsors: Record<string, string>;
  read: readonly string[];
}

/** One wording for the three states, so the two cards cannot drift apart. */
export function selfReportedText(view: SelfReportedView): string {
  if (view.state === "loading") return "checking the application…";
  if (view.state === "unavailable") return "could not be read from the tracker";
  return view.value.trim() || "not reported";
}

export function selfReportedFor(
  index: SelfReportedIndex | null,
  claim: { program: string; refNumber: string }
): SelfReportedView {
  if (!index) return { state: "loading" };
  // A program whose tracker did not answer is UNKNOWN, not blank. A failed
  // whole-request fetch stores an empty index, which lands here for every card.
  if (!index.read.includes(claim.program)) return { state: "unavailable" };
  return { state: "known", value: index.sponsors[claim.refNumber] ?? "" };
}

export function AdminView(): React.ReactElement {
  const [key, setKey] = React.useState(readStoredKey);
  const [keyDraft, setKeyDraft] = React.useState("");
  const [adminId, setAdminId] = React.useState("");
  const [claims, setClaims] = React.useState<ClaimSummary[]>([]);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [filter, setFilter] = React.useState<"all" | "conflicts" | "unpaid">("all");
  // Round 29. Deliberately SEPARATE from exportProgram below: a director
  // can view Girls State on screen while the export stays on All, and the
  // two are not meant to track each other. Do not merge them.
  const [viewProgram, setViewProgram] = React.useState<"all" | "boys" | "girls">("all");
  const [exportProgram, setExportProgram] = React.useState<"all" | "boys" | "girls">("all");
  const [downloading, setDownloading] = React.useState(false);
  // Round 32. Fetched separately from the claims list because it reads both
  // tracker workbooks — see the route comment. null means "not back yet".
  const [selfReported, setSelfReported] = React.useState<SelfReportedIndex | null>(null);

  /**
   * Fetches the CSV with the admin key in the HEADER, then saves the response
   * as a file.
   *
   * Deliberately not an <a href="/api/...?key=…"> download. A plain link cannot
   * set a header, so the key would have to travel in the query string — into
   * browser history, bookmarks and any Referer the page emits. That is exactly
   * what the sessionStorage/header choice at the top of this file avoids, and a
   * download button is not a good enough reason to undo it.
   */
  const downloadCsv = React.useCallback(async () => {
    setDownloading(true);
    setError("");
    try {
      const res = await fetch(`/api/admin/claims/export?program=${exportProgram}`, {
        headers: { "X-Admin-Key": key },
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Export failed (${res.status}).`);
      }
      const disposition = res.headers.get("Content-Disposition") ?? "";
      const named = /filename="([^"]+)"/.exec(disposition)?.[1];
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = named ?? `sponsorship-claims-${exportProgram}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoked on the next tick so the click has certainly been handled.
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDownloading(false);
    }
  }, [exportProgram, key]);

  const load = React.useCallback(async (k: string) => {
    setBusy(true);
    setError("");
    try {
      const out = await api<{ adminId: string; claims: ClaimSummary[] }>("/admin/claims", k);
      setAdminId(out.adminId);
      setClaims(out.claims);
      // AFTER the claims are on screen, and never allowed to fail the load:
      // this is one field on a card, and the list is useful without it. An
      // empty index is stored on failure rather than left null, so the cards
      // say "could not be read" instead of "checking…" forever.
      void api<SelfReportedIndex>("/admin/self-reported-sponsors", k)
        .then(setSelfReported)
        .catch(() => setSelfReported({ sponsors: {}, read: [] }));
      try {
        sessionStorage.setItem(KEY_STORAGE, k);
      } catch {
        /* private mode — the key simply won't persist across reloads */
      }
    } catch (e) {
      setError((e as Error).message);
      setAdminId("");
    } finally {
      setBusy(false);
    }
  }, []);

  React.useEffect(() => {
    if (key) void load(key);
  }, [key, load]);

  function signOut() {
    try {
      sessionStorage.removeItem(KEY_STORAGE);
    } catch {
      /* nothing to clear */
    }
    setKey("");
    setAdminId("");
    setClaims([]);
  }

  async function act(fn: () => Promise<{ claim: ClaimSummary }>) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const { claim } = await fn();
      setClaims((prev) => prev.map((c) => (c.itemId === claim.itemId ? claim : c)));
    } catch (e) {
      setError((e as Error).message);
      // Round 28. A second claim can arrive AFTER this page was loaded, so a
      // per-row action the admin could legitimately see may now be refused.
      // Reload rather than leaving them on a button that no longer applies:
      // the refreshed list renders the combined card for that applicant.
      if (e instanceof ApiError && typeof e.body.contest === "string") {
        setNotice(
          `This applicant now has competing claims. The list has been refreshed — ` +
            `resolve them together on the combined card for ${e.body.contest}.`
        );
        void load(key);
      }
    } finally {
      setBusy(false);
    }
  }

  if (!adminId) {
    return (
      <><ProgramHeader subtitle="Sponsorship Claim — Admin" />
      <main className="mx-auto max-w-md px-4 py-10">
        <div className="section-card">
          <h1 className="text-xl font-semibold text-program">Sponsorship Claim — Admin</h1>
          <p className="field-hint mt-1 mb-4">
            Enter your personal admin key. Each admin has their own, and every action
            you take is recorded against it.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setKey(keyDraft.trim());
            }}
          >
            <label className="field-label" htmlFor="adminkey">
              Admin key
            </label>
            <input
              id="adminkey"
              className="field-input font-mono"
              type="password"
              autoComplete="off"
              value={keyDraft}
              onChange={(e) => setKeyDraft(e.target.value)}
            />
            {error && <p className="field-error">{error}</p>}
            <button className="btn-primary mt-4 w-full" type="submit" disabled={busy}>
              {busy ? "Checking…" : "Sign in"}
            </button>
          </form>
        </div>
      </main></>
    );
  }

  // Round 29 — every count and card comes from ONE pure derivation, so the
  // two filter dimensions cannot disagree with each other. See planAdminList.
  const plan = planAdminList(claims, { viewProgram, filter });
  const { total, conflicts, unpaid } = plan;

  return (
    <><ProgramHeader subtitle="Sponsorship Claim — Admin" />
    <main className="mx-auto max-w-5xl px-4 py-8">
      <header className="mb-6 flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="text-xl font-semibold text-program">Sponsorship Claim — Admin</h1>
        <p className="text-sm text-slate-600">
          Signed in as <strong className="font-mono">{adminId}</strong>{" "}
          <button className="ml-2 underline" onClick={signOut} type="button">
            sign out
          </button>
        </p>
      </header>

      {error && (
        <div className="section-card border-red-300 bg-red-50">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {notice && (
        <div className="section-card border-amber-300 bg-amber-50">
          <p className="text-sm text-amber-900">{notice}</p>
        </div>
      )}

      {/*
        Channel A, above the filters: issuing a link is how a claim STARTS,
        so it sits ahead of the list of claims that already exist rather than
        buried among them.
      */}
      <IssueLinkCard adminKey={key} onIssued={() => void load(key)} />

      {/*
        Round 29 — the OUTER filter. Segmented buttons matching the status
        tabs below, because it filters the same list; the export selector
        further down keeps its muted radios so the two program pickers do
        not read as one control repeated.
      */}
      <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
        <span className="text-slate-600">Viewing:</span>
        {(
          [
            ["all", "All programs"],
            ["boys", "Boys State"],
            ["girls", "Girls State"],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setViewProgram(value)}
            className={
              viewProgram === value
                ? "rounded-md bg-program px-3 py-1 text-white"
                : "rounded-md border border-slate-300 bg-white px-3 py-1"
            }
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mb-4 flex gap-2 text-sm">
        {(
          [
            ["all", `All (${total})`],
            ["conflicts", `Conflicts (${conflicts})`],
            ["unpaid", `Unpaid (${unpaid})`],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setFilter(value)}
            className={
              filter === value
                ? "rounded-md bg-program px-3 py-1 text-white"
                : "rounded-md border border-slate-300 bg-white px-3 py-1"
            }
          >
            {label}
          </button>
        ))}
        <button
          type="button"
          className="ml-auto underline"
          onClick={() => void load(key)}
          disabled={busy}
        >
          {busy ? "Working…" : "Refresh"}
        </button>
      </div>

      {/*
        Full-contact CSV for reconciling sponsors and payments. Admin-only, and
        unlike the Post-facing outreach roster it deliberately carries contact
        detail — see claims-export.ts.
      */}
      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
        <span className="text-slate-600">Export claims:</span>
        {(
          [
            ["all", "All programs"],
            ["boys", "Boys State"],
            ["girls", "Girls State"],
          ] as const
        ).map(([value, label]) => (
          <label key={value} className="flex items-center gap-1">
            <input
              type="radio"
              name="exportProgram"
              checked={exportProgram === value}
              onChange={() => setExportProgram(value)}
            />
            {label}
          </label>
        ))}
        <button
          type="button"
          className="btn-secondary"
          disabled={busy || downloading}
          onClick={() => void downloadCsv()}
        >
          {downloading ? "Preparing…" : "Download CSV"}
        </button>
      </div>

      {plan.cards.length === 0 && (
        <div className="section-card">
          <p className="text-sm text-slate-600">Nothing to show.</p>
        </div>
      )}

      {plan.cards.map((card) =>
        card.kind === "contest" ? (
          <ContestCard
            key={card.refNumber}
            refNumber={card.refNumber}
            rows={card.rows}
            selfReported={selfReportedFor(selfReported, {
              program: card.rows[0]?.program ?? "",
              refNumber: card.refNumber,
            })}
            busy={busy}
            adminKey={key}
            onDone={(result) => {
              setNotice(describeContestOutcome(result));
              void load(key);
            }}
          />
        ) : (
          <ClaimCard
            key={card.claim.itemId}
            claim={card.claim}
            selfReported={selfReportedFor(selfReported, card.claim)}
            busy={busy}
            adminKey={key}
            onAct={act}
            siblings={card.siblings}
          />
        )
      )}
    </main></>
  );
}

export function ClaimCard({
  claim,
  busy,
  adminKey,
  onAct,
  siblings = [],
  selfReported,
}: {
  claim: ClaimSummary;
  busy: boolean;
  adminKey: string;
  onAct: (fn: () => Promise<{ claim: ClaimSummary }>) => Promise<void>;
  /**
   * Other CLAIMED rows for the same reference number (Round 27). Defaults to
   * none so existing callers and tests construct unchanged.
   */
  siblings?: readonly ClaimSummary[];
  /**
   * The family's own sponsor entry (Round 32). OPTIONAL, and omitted entirely
   * when absent rather than defaulted: a card that has not been given the value
   * knows nothing, and printing "not reported" off a missing prop would be the
   * same false certainty this field exists to remove.
   */
  selfReported?: SelfReportedView;
}): React.ReactElement {
  const [choice, setChoice] = React.useState<"self-reported" | "claimed" | "custom">("claimed");
  const [customValue, setCustomValue] = React.useState("");
  const [note, setNote] = React.useState("");
  // Round 26 item 4. Local to this claim's panel and cleared after a submit,
  // so suppression can never carry over to the next conflict.
  const [suppress, setSuppress] = React.useState(false);
  const [suppressReason, setSuppressReason] = React.useState("");
  // Round 27 follow-up. Reveals the SAME resolve panel on a row that is already
  // resolved. Collapsed by default so a settled row still reads as settled.
  const [changing, setChanging] = React.useState(false);

  /**
   * A row that has been claimed and whose conflict is no longer flagged.
   *
   * resolveConflict() has always been re-runnable — its own docstring says an
   * admin who picks the wrong side must be able to correct it, and phase6 tests
   * the re-resolution path. But the panel below was gated on conflictFlagged,
   * and resolving sets that to "No", so the UI deleted its own entry point at
   * the exact moment a director most needs it: right after a wrong resolution.
   *
   * A row still at "issued" is deliberately excluded — there is no claim to
   * resolve yet.
   */
  const resolved = !claim.conflictFlagged && claim.status === "claimed";
  const showPanel = claim.conflictFlagged || (resolved && changing);

  return (
    <div className="section-card">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-mono text-lg font-semibold">{claim.refNumber}</h2>
        <div className="flex gap-2 text-xs">
          <span className="rounded bg-slate-100 px-2 py-1">{claim.program}</span>
          <span className="rounded bg-slate-100 px-2 py-1">Channel {claim.channel}</span>
          <span className="rounded bg-slate-100 px-2 py-1">{claim.status}</span>
          {claim.conflictFlagged && (
            <span className="rounded bg-amber-100 px-2 py-1 font-semibold text-amber-900">
              conflict
            </span>
          )}
          {/*
            A CUE, not a workflow. The director already has full contact
            details and can act manually; this only makes the row findable
            when scanning a long list. The email is the push, this is the pull.
          */}
          {claim.familyContact === "not-yet" && (
            <span className="rounded bg-amber-100 px-2 py-1 font-semibold text-amber-900">
              wants intro
            </span>
          )}
          {claim.notificationSuppressed && (
            <span className="rounded bg-red-50 px-2 py-1 font-semibold text-red-700">
              not notified
            </span>
          )}
        </div>
      </div>

      <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
        <Row label="Sponsor" value={claim.entity} />
        <Row label="Contact" value={`${claim.contactName} · ${claim.contactEmail}`} />
        {/*
          Round 30. Sits with Contact rather than Payment: this is about the
          sponsor's relationship to the student, not money. Blank on claims
          submitted before this field existed, and rendered as NOTHING in that
          case so an old row does not look defective.
        */}
        {claim.familyContact && (
          <Row
            label="Family contact"
            value={
              familyContactLabel(claim.familyContact) +
              (claim.familyContactOther ? ` — ${claim.familyContactOther}` : "")
            }
          />
        )}
        <Row
          label="Payment"
          value={
            claim.paymentReceived
              ? `${claim.paymentMethod} — RECEIVED, marked by ${claim.paymentReceivedBy} on ${shortDate(claim.paymentReceivedAt)}`
              : `${claim.paymentMethod || "—"} — pending${claim.squareOrderNumber ? ` (order ${claim.squareOrderNumber})` : ""}`
          }
        />
        <Row label="Claimed" value={shortDate(claim.submittedAt)} />
        {claim.paymentNote && <Row label="Payment note" value={claim.paymentNote} />}
        {claim.notes && <Row label="Notes" value={claim.notes} />}
        {/*
          Round 27. Without this the card shows only its OWN entity, so a
          director choosing between "the family's entry" and "the Post's claim"
          would have no idea a third party had also claimed this student — and
          "custom" only helps someone who already knows to reach for it.
        */}
        {siblings.length > 0 && (
          <Row
            label="Also claimed by"
            value={siblings
              .map(
                (s) =>
                  `${s.entity} · ${s.contactName || "(no contact name)"} · ` +
                  `${s.conflictFlagged ? "unresolved" : "resolved"}`
              )
              .join("; ")}
          />
        )}
        {/*
          Round 32. The resolve panel below offers "Use the family's entry" —
          and until now the card never showed what that entry WAS, so half of
          the choice was invisible and a director was picking blind.

          Placed here on purpose: last of the sponsor-identity rows, so it reads
          Sponsor -> Also claimed by -> Family's entry and lands directly above
          the options it informs. Deliberately NOT next to "Family contact"
          above, which is an unrelated Round 30 field about the sponsor's
          contact with the family — see SelfReportedView.

          Shown on every card, including "not reported", matching how this view
          already treats "Also claimed by" and "Sponsor notified": state is
          stated plainly and always, not hidden until it becomes contentious.
        */}
        {selfReported && (
          <Row label="Family's entry" value={selfReportedText(selfReported)} />
        )}
        {claim.conflictResolvedBy && (
          <Row
            label="Conflict resolved"
            value={`by ${claim.conflictResolvedBy} on ${shortDate(claim.conflictResolvedAt)} — ${claim.conflictResolution}`}
          />
        )}
        {/*
          Round 26 item 4. Without this a suppressed claim reads exactly like a
          normally resolved one, and the attribution written to the row is
          invisible to whoever picks the claim up next — which would defeat the
          point of recording it.
        */}
        {claim.notificationSuppressed && (
          <Row
            label="Notification suppressed"
            value={
              `by ${claim.suppressedBy || "(unknown)"}` +
              `${claim.suppressedAt ? ` on ${shortDate(claim.suppressedAt)}` : ""}` +
              `${claim.suppressReason ? ` — ${claim.suppressReason}` : ""}`
            }
          />
        )}
        {!claim.notificationSuppressed && claim.resolutionNotifiedAt && (
          <Row label="Sponsor notified" value={shortDate(claim.resolutionNotifiedAt)} />
        )}
      </dl>

      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-200 pt-4">
        <button
          type="button"
          className={claim.paymentReceived ? "btn-secondary" : "btn-primary"}
          disabled={busy}
          onClick={() =>
            void onAct(() =>
              api<{ claim: ClaimSummary }>(`/admin/claims/${claim.itemId}/payment`, adminKey, {
                method: "POST",
                body: JSON.stringify({ received: !claim.paymentReceived }),
              })
            )
          }
        >
          {claim.paymentReceived ? "Undo payment received" : "Mark payment received"}
        </button>

        {/*
          Deliberately low-key — a plain text button, not a second primary
          action. Changing a settled resolution is a correction, not part of
          the normal flow, and it should not compete with the payment button
          for attention on every claimed row.
        */}
        {resolved && (
          <button
            type="button"
            className="ml-auto text-sm underline"
            disabled={busy}
            onClick={() => setChanging((v) => !v)}
          >
            {changing ? "Cancel" : "Change resolution"}
          </button>
        )}
      </div>

      {showPanel && (
        <div
          className={
            claim.conflictFlagged
              ? "mt-4 rounded-md border border-amber-300 bg-amber-50 p-4"
              : "mt-4 rounded-md border border-slate-300 bg-slate-50 p-4"
          }
        >
          {claim.conflictFlagged ? (
            <>
              <p className="text-sm font-semibold text-amber-900">Resolve sponsor conflict</p>
              <p className="mt-1 text-xs text-amber-800">
                The family and the claiming Post disagree. Choose which becomes the
                authoritative sponsor. Nothing is blocked either way — the applicant can
                already be finalized.
              </p>
            </>
          ) : (
            <>
              <p className="text-sm font-semibold text-slate-800">Change resolution</p>
              <p className="mt-1 text-xs text-slate-600">
                Currently recorded as sponsor:{" "}
                <strong>{claim.conflictResolution || claim.entity}</strong>. Changing this
                overwrites the authoritative value and emails this sponsor the new
                outcome.
              </p>
            </>
          )}
          <div className="mt-3 space-y-1 text-sm">
            {(
              [
                ["claimed", "Use the Post's claim"],
                ["self-reported", "Use the family's entry"],
                ["custom", "Enter a different value"],
              ] as const
            ).map(([value, label]) => (
              <label key={value} className="flex items-center gap-2">
                <input
                  type="radio"
                  name={`choice-${claim.itemId}`}
                  checked={choice === value}
                  onChange={() => setChoice(value)}
                />
                {label}
              </label>
            ))}
          </div>
          {choice === "custom" && (
            <input
              className="field-input mt-2"
              placeholder="e.g. Post 40"
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
            />
          )}
          <input
            className="field-input mt-2"
            placeholder="Note (optional) — e.g. confirmed by phone"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />

          {/*
            Round 26 item 4. Resolving normally emails the claiming sponsor
            either way; this withholds that one email for a suspected bad
            actor. Unchecked by default, and the reason is required — a
            silence that nobody has to explain is the thing to avoid here.
          */}
          <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3">
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={suppress}
                onChange={(e) => setSuppress(e.target.checked)}
              />
              <span>
                <span className="font-medium">
                  Do not email this sponsor — suspected fraudulent or invalid claim
                </span>
                <span className="mt-0.5 block text-xs text-slate-600">
                  Leave unchecked to notify them of the outcome, which is the normal
                  case. The conflict is still resolved either way.
                </span>
              </span>
            </label>
            {suppress && (
              <>
                <input
                  className="field-input mt-2"
                  placeholder="Reason (required) — recorded against your admin id"
                  value={suppressReason}
                  onChange={(e) => setSuppressReason(e.target.value)}
                />
                {!suppressReason.trim() && (
                  <p className="field-error">A reason is required to suppress the email.</p>
                )}
              </>
            )}
          </div>

          <button
            type="button"
            className="btn-primary mt-3"
            disabled={busy || (suppress && !suppressReason.trim())}
            onClick={() =>
              void onAct(async () => {
                const result = await api<{ claim: ClaimSummary }>(
                  `/admin/claims/${claim.itemId}/resolve-conflict`,
                  adminKey,
                  {
                    method: "POST",
                    body: JSON.stringify({
                      choice,
                      customValue,
                      note,
                      suppressNotification: suppress,
                      suppressReason,
                    }),
                  }
                );
                // Never sticky. Without this the checkbox stays set while the
                // admin works down a list of conflicts, and the next claim gets
                // silenced by a box nobody re-read.
                setSuppress(false);
                setSuppressReason("");
                // Collapse again, so a corrected row reads as settled rather
                // than sitting open inviting another change.
                setChanging(false);
                return result;
              })
            }
          >
            {suppress
              ? "Set sponsor, do not email"
              : claim.conflictFlagged
                ? "Set authoritative sponsor"
                : "Change authoritative sponsor"}
          </button>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div className="flex gap-2">
      <dt className="shrink-0 text-slate-500">{label}</dt>
      <dd className="text-slate-800">{value}</dd>
    </div>
  );
}

/**
 * A reference with competing claims (Round 28).
 *
 * Rendered INSTEAD of the individual cards, because N claims are N competing
 * answers to one question — not N independent decisions. Resolving them
 * separately is what let the second resolution silently overwrite the first and
 * told two Posts they had both won the same student.
 *
 * Nothing is pre-selected. The old per-row panel defaulted to "Use the Post's
 * claim", which made the lowest-effort path — open, submit — the wrong one for
 * every row after the first.
 */
export function ContestCard({
  refNumber,
  rows,
  busy,
  adminKey,
  onDone,
  selfReported,
}: {
  refNumber: string;
  rows: readonly ClaimSummary[];
  busy: boolean;
  adminKey: string;
  onDone: (result: ContestApiResult) => void;
  /** Same field, same rules as ClaimCard's — this card offers the same choice. */
  selfReported?: SelfReportedView;
}): React.ReactElement {
  const [winner, setWinner] = React.useState<string>("");
  const [customValue, setCustomValue] = React.useState("");
  const [note, setNote] = React.useState("");
  const [suppress, setSuppress] = React.useState<Record<string, string>>({});
  const [working, setWorking] = React.useState(false);
  // Matches ClaimCard's pattern (9d8f32d): a settled contest reads as
  // settled, and the full form is reached deliberately rather than sitting
  // open and selectable over a decision already made.
  const [changing, setChanging] = React.useState(false);

  const resolved = contestIsResolved(rows);
  const recordedSponsor = parseRecordedSponsor(rows);
  const showForm = !resolved || changing;
  const chosenCustom = winner === "custom";
  const ready = winner !== "" && (!chosenCustom || customValue.trim() !== "");
  const suppressIncomplete = Object.values(suppress).some((r) => !r.trim());

  return (
    <div className={resolved ? "section-card" : "section-card border-amber-300"}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-mono text-lg font-semibold">{refNumber}</h2>
        <span
          className={
            resolved
              ? "rounded bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700"
              : "rounded bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-900"
          }
        >
          {rows.length} claims{resolved ? " · resolved" : " competing"}
        </span>
      </div>

      {resolved ? (
        <>
          <p className="mt-2 text-sm text-slate-700">
            <strong>Sponsor: {recordedSponsor || "(not recorded)"}</strong>
          </p>
          {rows[0]?.conflictResolvedBy && (
            <p className="field-hint mt-1">
              Resolved by {rows[0].conflictResolvedBy} on {shortDate(rows[0].conflictResolvedAt)}
            </p>
          )}
          {/*
            Both claims stay listed. Who ELSE claimed this applicant is exactly
            what Round 27 exists to preserve, and it should not disappear the
            moment the contest is settled.
          */}
          <dl className="mt-3 space-y-1 text-sm">
            {rows.map((r) => (
              <div key={r.itemId} className="flex flex-wrap gap-2">
                <span className="text-slate-700">{r.entity}</span>
                <span className="text-slate-500">
                  {"— "}
                  {r.contactName || "(no contact name)"}
                  {" · "}
                  {r.conflictResolution.includes("(selected)") ? (
                    <strong className="text-slate-800">selected</strong>
                  ) : (
                    "not selected"
                  )}
                </span>
              </div>
            ))}
          </dl>
        </>
      ) : (
        <>
          <p className="mt-2 text-sm text-slate-700">
            <strong>{rows.length} organizations have claimed this applicant.</strong> Choose
            which one is the sponsor. Your choice is recorded as the authoritative sponsor
            and every organization below is told the outcome.
          </p>
          {recordedSponsor && (
            <p className="field-hint mt-1">Currently recorded: {recordedSponsor}</p>
          )}
        </>
      )}

      {/*
        Round 32, and the same gap as ClaimCard's: "The family's entry" is one
        of the options below. For a contested reference THIS is the card the
        director actually lands on, so leaving it out here would have left the
        hole open in the path Round 28 made the primary one.
      */}
      {selfReported && (
        <dl className="mt-3 text-sm">
          <Row label="Family's entry" value={selfReportedText(selfReported)} />
        </dl>
      )}

      {resolved && (
        <button
          type="button"
          className="mt-3 text-sm underline"
          disabled={busy || working}
          onClick={() => setChanging((v) => !v)}
        >
          {changing ? "Cancel" : "Change resolution"}
        </button>
      )}

      {showForm && (
      <>

      <p className="mt-4 text-sm font-semibold text-slate-800">Who is the sponsor?</p>
      <div className="mt-2 space-y-2">
        {rows.map((r) => (
          <div key={r.itemId} className="rounded-md border border-slate-200 p-3">
            <label className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                className="mt-1"
                name={`winner-${refNumber}`}
                checked={winner === r.itemId}
                onChange={() => setWinner(r.itemId)}
              />
              <span>
                <span className="font-medium">{r.entity}</span>
                {/*
                  Round 33. PER ROW, not on the card header — the header speaks
                  for the whole contest, and a "wants intro" badge up there
                  could not say WHICH of the competing sponsors asked. Same cue
                  as ClaimCard's, in the only place it can be unambiguous.
                */}
                {r.familyContact === "not-yet" && (
                  <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-900">
                    wants intro
                  </span>
                )}
                <span className="mt-0.5 block text-xs text-slate-600">
                  {r.contactName || "(no contact name)"} · {r.contactEmail} · claimed{" "}
                  {shortDate(r.submittedAt)}
                </span>
                {/*
                  Round 33, and the THIRD field found missing from this card
                  after "Family's entry" and Notes: Round 30's answer about the
                  sponsor's contact with the student's family.
                  
                  It bears directly on the decision being made here — a sponsor
                  who already knows the family is a different proposition from
                  one sending money alone — and on a contested reference this
                  card is the only place a director looks. Blank on claims
                  submitted before Round 30, and rendered as NOTHING in that
                  case so an old row does not look defective.
                */}
                {r.familyContact && (
                  <span className="mt-0.5 block text-xs text-slate-600">
                    Family contact: {familyContactLabel(r.familyContact)}
                    {r.familyContactOther ? ` — ${r.familyContactOther}` : ""}
                  </span>
                )}
                {/*
                  Round 33. What this sponsor WROTE, on the row it belongs to.
                  ClaimCard has always shown Notes above its resolve options;
                  this card never did, so on a contested reference — where two
                  organizations are arguing for the same student and the whole
                  decision is a judgment call — the one place either of them
                  explained themselves was silently dropped. "The applicant is
                  my grandson and a member of our Squadron" is exactly the kind
                  of thing that should reach the person deciding, and it did
                  not.

                  Per row, never merged: two notes concatenated would read as
                  one organization's case and attribute half of it to the
                  wrong claimant.
                */}
                {r.notes && (
                  <span className="mt-1 block text-xs text-slate-700">
                    <span className="font-medium">Notes:</span> {r.notes}
                  </span>
                )}
              </span>
            </label>

            {/* Per row, never contest-wide — see ResolveContestInput. */}
            <label className="mt-2 flex items-center gap-2 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={r.itemId in suppress}
                onChange={(e) =>
                  setSuppress((prev) => {
                    const next = { ...prev };
                    if (e.target.checked) next[r.itemId] = "";
                    else delete next[r.itemId];
                    return next;
                  })
                }
              />
              Do not email this one — suspected fraudulent or invalid
            </label>
            {r.itemId in suppress && (
              <>
                <input
                  className="field-input mt-1"
                  placeholder="Reason (required)"
                  value={suppress[r.itemId]}
                  onChange={(e) =>
                    setSuppress((prev) => ({ ...prev, [r.itemId]: e.target.value }))
                  }
                />
                {!suppress[r.itemId].trim() && (
                  <p className="field-error">A reason is required to suppress the email.</p>
                )}
              </>
            )}
          </div>
        ))}

        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name={`winner-${refNumber}`}
            checked={winner === "self-reported"}
            onChange={() => setWinner("self-reported")}
          />
          The family&apos;s entry
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name={`winner-${refNumber}`}
            checked={chosenCustom}
            onChange={() => setWinner("custom")}
          />
          Enter a different value
        </label>
        {chosenCustom && (
          <input
            className="field-input"
            placeholder="e.g. Post 40"
            value={customValue}
            onChange={(e) => setCustomValue(e.target.value)}
          />
        )}
      </div>

      <input
        className="field-input mt-3"
        placeholder="Note (optional) — e.g. confirmed by phone"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />

      <button
        type="button"
        className="btn-primary mt-3"
        disabled={busy || working || !ready || suppressIncomplete}
        onClick={() => {
          setWorking(true);
          void (async () => {
            try {
              const body = {
                winner:
                  winner === "self-reported"
                    ? { kind: "self-reported" }
                    : winner === "custom"
                      ? { kind: "custom", value: customValue }
                      : { kind: "claim", itemId: winner },
                note,
                suppress: Object.entries(suppress).map(([itemId, reason]) => ({
                  itemId,
                  reason,
                })),
              };
              const out = await api<ContestApiResult>(
                `/admin/references/${encodeURIComponent(refNumber)}/resolve-contest`,
                adminKey,
                { method: "POST", body: JSON.stringify(body) }
              );
              setChanging(false);
              onDone(out);
            } catch (e) {
              onDone({ error: (e as Error).message });
            } finally {
              setWorking(false);
            }
          })();
        }}
      >
        {working ? "Recording…" : `Record sponsor and notify all ${rows.length}`}
      </button>
      </>
      )}
    </div>
  );
}

export interface ContestApiResult {
  referenceNumber?: string;
  finalValue?: string;
  rows?: {
    itemId: string;
    entity: string;
    outcome: "selected" | "not-selected";
    notified: boolean;
    skipped?: "unchanged" | "suppressed";
    error?: string;
  }[];
  error?: string;
}

/** The sentence shown after a contest is recorded. */
export function describeContestOutcome(result: ContestApiResult): string {
  if (result.error) return result.error;
  const rows = result.rows ?? [];
  const notified = rows.filter((r) => r.notified).length;
  const unchanged = rows.filter((r) => r.skipped === "unchanged").length;
  const suppressed = rows.filter((r) => r.skipped === "suppressed").length;
  const failed = rows.filter((r) => r.error);

  let out = `Recorded ${result.finalValue} as the sponsor for ${result.referenceNumber}. `;
  out += `Notified ${notified} organization${notified === 1 ? "" : "s"}.`;
  if (unchanged) {
    out +=
      ` ${unchanged} ${unchanged === 1 ? "was" : "were"} already told this outcome and ` +
      `${unchanged === 1 ? "was" : "were"} not emailed again.`;
  }
  if (suppressed) out += ` ${suppressed} suppressed.`;
  if (failed.length) {
    out +=
      ` ${failed.length} of ${rows.length} could not be updated — ` +
      `${failed.map((f) => f.entity).join(", ")}. The sponsor value is saved; retry to finish notifying.`;
  }
  return out;
}

/**
 * The sponsor a resolved contest recorded, for the collapsed summary.
 *
 * Two sources, in order:
 *
 * 1. The row marked "(selected)" — its own `entity` is used verbatim, so
 *    nothing is parsed out of the stored value at all. This covers every
 *    contest won by one of the claims, which is the ordinary case.
 *
 * 2. Otherwise the family's entry or a custom value won, so no row is
 *    selected and the value exists only inside the resolution string.
 *
 * THE REGEX BELOW PARSES A FORMAT WE ALSO WRITE. It is produced by
 * contestResolutionText() in server/contest.ts, called from resolveContest()
 * in server/admin-service.ts — `contest -> "VALUE" (selected|not-selected)`,
 * optionally followed by an em-dash note. AdminView.test.tsx asserts this
 * parser against that function's REAL output, so changing the format there
 * fails a test rather than quietly degrading this to the raw string.
 */
export function parseRecordedSponsor(rows: readonly ClaimSummary[]): string {
  const winningRow = rows.find((r) => r.conflictResolution.includes("(selected)"));
  if (winningRow) return winningRow.entity;

  for (const r of rows) {
    const quoted = /^contest -> "([^"]*)"/.exec(r.conflictResolution);
    if (quoted?.[1]) return quoted[1];
  }
  // Nothing recognizable. Better to say so than to print a storage format at
  // a director as though it were a sponsor's name.
  return "";
}

/** Whether a contest has been settled, and so should render collapsed. */
export function contestIsResolved(rows: readonly ClaimSummary[]): boolean {
  // Any flagged row means a decision is outstanding — Round 27 re-flags every
  // row when a new competing claim arrives, so a reopened contest asks again.
  if (rows.some((r) => r.conflictFlagged)) return false;
  // Unflagged but never resolved is the edge where Round 27's best-effort flag
  // write failed. Render OPEN: a contest nobody has answered must not look
  // settled just because the flag is missing.
  return rows.some((r) => Boolean(r.conflictResolvedAt));
}

export type ViewProgram = "all" | "boys" | "girls";
export type StatusFilter = "all" | "conflicts" | "unpaid";

export type AdminCard =
  | { kind: "contest"; refNumber: string; rows: ClaimSummary[] }
  | { kind: "claim"; claim: ClaimSummary; siblings: ClaimSummary[] };

export interface AdminListPlan {
  /** Counts for the status tabs, all derived from the SAME program-filtered base. */
  total: number;
  conflicts: number;
  unpaid: number;
  cards: AdminCard[];
}

/**
 * What the admin list renders, for a given pair of filters (Round 29).
 *
 * TWO INDEPENDENT DIMENSIONS: program and status. Program is the OUTER one —
 * every count, every card and every contest derives from the program-filtered
 * base, so a status tab always describes what it will actually show. Before
 * this, Conflicts and Unpaid were counted from the UNFILTERED list while the
 * rendered rows were filtered; harmless while status was the only dimension,
 * but a second dimension would have made a tab promise a count it could not
 * deliver.
 *
 * Contests are grouped AFTER the program filter, never before, and receive
 * their FULL row set regardless of the status filter. A status filter hiding
 * one row would make a contested claim look uncontested, which defeats the
 * point of surfacing the contest. The status filter decides whether a contest
 * card APPEARS; it never decides how much of the contest that card shows.
 *
 * Pure and exported so all of the above is testable — the component itself
 * cannot be rendered far enough to exercise it without a signed-in session.
 */
export function planAdminList(
  claims: readonly ClaimSummary[],
  opts: { viewProgram: ViewProgram; filter: StatusFilter }
): AdminListPlan {
  const inProgram = claims.filter(
    (c) => opts.viewProgram === "all" || c.program === opts.viewProgram
  );

  const shown = inProgram.filter((c) =>
    opts.filter === "conflicts"
      ? c.conflictFlagged
      : opts.filter === "unpaid"
        ? !c.paymentReceived
        : true
  );

  // A reference carries its own program in the prefix (MDBS-/MDGS-), so every
  // claim on one reference is always in the same program and a program filter
  // can only ever take a contest whole. Grouping from inProgram also means an
  // empty contest card is not representable: the rows come from the same array
  // the grouping counted.
  const claimedByRef = new Map<string, ClaimSummary[]>();
  for (const c of inProgram) {
    if (c.status !== "claimed") continue;
    const list = claimedByRef.get(c.refNumber) ?? [];
    list.push(c);
    claimedByRef.set(c.refNumber, list);
  }

  const cards: AdminCard[] = [];
  const seenContest = new Set<string>();
  for (const c of shown) {
    const contestRows = claimedByRef.get(c.refNumber);
    if (contestRows && contestRows.length > 1) {
      if (seenContest.has(c.refNumber)) continue;
      seenContest.add(c.refNumber);
      cards.push({ kind: "contest", refNumber: c.refNumber, rows: contestRows });
      continue;
    }
    cards.push({
      kind: "claim",
      claim: c,
      siblings: (claimedByRef.get(c.refNumber) ?? []).filter((x) => x.itemId !== c.itemId),
    });
  }

  return {
    total: inProgram.length,
    conflicts: inProgram.filter((c) => c.conflictFlagged).length,
    unpaid: inProgram.filter((c) => !c.paymentReceived).length,
    cards,
  };
}
